import { create } from 'zustand';

/**
 * Cookie consent (GDPR / ePrivacy). Essential cookies (session, sign-in) need no consent;
 * analytics only runs after the visitor allows it, and the choice can be changed any time.
 */
export interface Consent {
  analytics: boolean;
  /** When the choice was made (ISO); absent until the visitor decides. */
  at?: string;
}

const KEY = 'bm.consent';
/** Bump when the purposes change so everyone is asked again. */
const VERSION = 1;

function read(): Consent | null {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (raw && raw.v === VERSION) return { analytics: !!raw.analytics, at: raw.at };
  } catch {
    /* blocked storage: ask again */
  }
  return null;
}

interface ConsentState {
  consent: Consent | null;
  /** The settings dialog (opened from the banner, the footer or account settings). */
  settingsOpen: boolean;
  decide(c: { analytics: boolean }): void;
  openSettings(open?: boolean): void;
}

export const useConsent = create<ConsentState>((set) => ({
  consent: read(),
  settingsOpen: false,
  decide({ analytics }) {
    const consent = { analytics, at: new Date().toISOString() };
    try {
      localStorage.setItem(KEY, JSON.stringify({ v: VERSION, ...consent }));
    } catch {
      /* keep it for this visit */
    }
    set({ consent, settingsOpen: false });
  },
  openSettings(open = true) {
    set({ settingsOpen: open });
  },
}));
